import { useEffect, useRef, useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const OVERLAYS = { nenhuma: '', clara: 'bg-white/60', escura: 'bg-black/50' };
const ALINH = { esquerda: 'items-start text-left', centro: 'items-center text-center', direita: 'items-end text-right' };
const POSV = { topo: 'justify-start pt-6 sm:pt-10', centro: 'justify-center', base: 'justify-end pb-8 sm:pb-12' };

export function bannerVisivel(b, agora = new Date()) {
  if (!b.ativo) return false;
  if (b.inicio && new Date(b.inicio) > agora) return false;
  if (b.fim && new Date(b.fim) < agora) return false;
  return true;
}

export default function BannerCarousel({ banners, intervaloSeg = 6, onDestino }) {
  const [indice, setIndice] = useState(0);
  const [direcao, setDirecao] = useState(1);
  const [pausado, setPausado] = useState(false);
  const toqueX = useRef(null);
  const reduzMovimento = useRef(
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ).current;

  const total = banners.length;
  const ir = useCallback(
    (prox, dir = 1) => {
      setDirecao(dir);
      setIndice((i) => (i + prox + total) % total);
    },
    [total]
  );

  useEffect(() => {
    if (total <= 1 || pausado || reduzMovimento) return;
    const t = setInterval(() => ir(1, 1), Math.max(3, intervaloSeg) * 1000);
    return () => clearInterval(t);
  }, [total, pausado, intervaloSeg, ir, reduzMovimento]);

  if (total === 0) return null;
  const b = banners[indice];
  const clicavel = b.destinoTipo && b.destinoTipo !== 'nenhum';

  const conteudo = (
    <div className="relative">
      <picture>
        {b.imagemMobile && <source media="(max-width: 640px)" srcSet={b.imagemMobile} />}
        <img
          src={b.imagemDesktop}
          alt={b.alt || b.titulo || b.nomeInterno}
          loading={indice === 0 ? 'eager' : 'lazy'}
          className="block h-auto w-full max-w-full object-contain"
        />
      </picture>
      {b.modo === 'personalizado' && (
        <div className={`absolute inset-0 ${OVERLAYS[b.overlay] || ''}`}>
          <div className={`mx-auto flex h-full max-w-7xl flex-col px-5 sm:px-10 ${POSV[b.posicaoVertical] || POSV.centro}`}>
            <div className={`flex max-w-xl flex-col gap-2 sm:gap-3 ${ALINH[b.alinhamento] || ALINH.esquerda}`}>
              {b.titulo && (
                <h2 className="font-heading text-2xl font-extrabold leading-tight sm:text-4xl" style={{ color: b.corTitulo || '#ffffff' }}>
                  {b.titulo}
                </h2>
              )}
              {b.subtitulo && (
                <p className="text-sm font-semibold sm:text-lg" style={{ color: b.corTexto || '#ffffff' }}>
                  {b.subtitulo}
                </p>
              )}
              {b.descricao && (
                <p className="text-xs sm:text-sm" style={{ color: b.corTexto || '#ffffff' }}>
                  {b.descricao}
                </p>
              )}
              {(b.botaoPrimario || b.botaoSecundario) && (
                <div className="mt-2 flex flex-wrap gap-2.5">
                  {b.botaoPrimario && (
                    <span
                      data-testid={`banner-cta-${indice}`}
                      className="rounded-full px-5 py-2.5 text-sm font-bold shadow-lg"
                      style={{ backgroundColor: b.corBotao || '#E63946', color: b.corBotaoTexto || '#ffffff' }}
                    >
                      {b.botaoPrimario}
                    </span>
                  )}
                  {b.botaoSecundario && (
                    <span className="rounded-full border-2 px-5 py-2.5 text-sm font-bold" style={{ borderColor: b.corTitulo || '#fff', color: b.corTitulo || '#fff' }}>
                      {b.botaoSecundario}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <section
      data-testid="banner-carousel"
      className="relative w-full select-none overflow-hidden bg-mk-blue"
      onMouseEnter={() => setPausado(true)}
      onMouseLeave={() => setPausado(false)}
      onTouchStart={(e) => { toqueX.current = e.touches[0].clientX; setPausado(true); }}
      onTouchEnd={(e) => {
        setPausado(false);
        if (toqueX.current == null) return;
        const delta = e.changedTouches[0].clientX - toqueX.current;
        if (Math.abs(delta) > 40) ir(delta < 0 ? 1 : -1, delta < 0 ? 1 : -1);
        toqueX.current = null;
      }}
      aria-roledescription="carrossel"
      aria-label="Banners promocionais"
    >
      <AnimatePresence initial={false} custom={direcao} mode="popLayout">
        <motion.div
          key={indice}
          data-testid={`banner-slide-${indice}`}
          className={clicavel ? 'cursor-pointer' : ''}
          initial={reduzMovimento ? { opacity: 0 } : { x: direcao * 320, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={reduzMovimento ? { opacity: 0 } : { x: direcao * -320, opacity: 0 }}
          transition={{ duration: reduzMovimento ? 0.15 : 0.55, ease: [0.22, 1, 0.36, 1] }}
          onClick={() => clicavel && onDestino(b)}
          role={clicavel ? 'link' : undefined}
        >
          {conteudo}
        </motion.div>
      </AnimatePresence>

      {total > 1 && (
        <>
          <button
            data-testid="banner-prev"
            onClick={(e) => { e.stopPropagation(); ir(-1, -1); }}
            aria-label="Banner anterior"
            className="absolute left-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/80 p-2 text-mk-blue shadow-md backdrop-blur transition-colors hover:bg-white"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            data-testid="banner-next"
            onClick={(e) => { e.stopPropagation(); ir(1, 1); }}
            aria-label="Próximo banner"
            className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/80 p-2 text-mk-blue shadow-md backdrop-blur transition-colors hover:bg-white"
          >
            <ChevronRight size={20} />
          </button>
          <div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 gap-1.5">
            {banners.map((_, i) => (
              <button
                key={i}
                data-testid={`banner-dot-${i}`}
                onClick={(e) => { e.stopPropagation(); setDirecao(i > indice ? 1 : -1); setIndice(i); }}
                aria-label={`Ir para banner ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-300 ${i === indice ? 'w-6 bg-white' : 'w-2 bg-white/50 hover:bg-white/80'}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
